import { useMemo } from 'react';
import { AutomationRunActionPromptType, WorkflowEvent, WorkflowEventTriggerType } from 'shared/types/graphql';
import { useAppSelector } from 'state/hooks';
import { childAutomationEventTypes } from '../state/utils/draft';
import { NextStepSelectValue } from '../types/NextStepSelectValue';
import { getStepValueDisplayProps, StepValueDisplayProps } from '../utils/getStepValueDisplayProps';
import { isConditionalChildAutomation } from '../utils/isConditionalChildAutomation';
import { useGetAutomationsList } from './useGetAutomationsList';

interface UseGetNextStepDisplayPropsInput {
  promptType: AutomationRunActionPromptType;
}

export const useGetNextStepDisplayProps = ({
  promptType,
}: UseGetNextStepDisplayPropsInput): { options: StepValueDisplayProps[]; isLoading: boolean } => {
  const isGlobalEditor = useAppSelector((state) => state.automations.isGlobalEditor);

  const eventTypes = useMemo(() => {
    const types = [...childAutomationEventTypes];

    // Add for H2H automations
    if (isGlobalEditor) {
      types.push(WorkflowEvent.InvoiceLineItemCreated);
    }

    return types;
  }, [isGlobalEditor]);

  const { automationsList: childAutomations, isLoading } = useGetAutomationsList({
    triggerType: WorkflowEventTriggerType.Manual,
    inEventType: eventTypes as WorkflowEvent[],
  });

  const childAutomationOptions = useMemo(() => {
    if (!childAutomations) return [];

    const options: StepValueDisplayProps[] = childAutomations.map((automation) => {
      return {
        ...getStepValueDisplayProps(automation.id),
        label: `${automation.name}${automation.isPublished ? '' : ' (Inactive)'}`,
        isConditionalChild: isConditionalChildAutomation(automation),
      };
    });

    return options;
  }, [childAutomations]);

  const options = useMemo(() => {
    if (promptType === AutomationRunActionPromptType.Statement) {
      return [
        {
          ...getStepValueDisplayProps(NextStepSelectValue.EndAutomation),
        },
      ];
    }
    return [
      NextStepSelectValue.SendAMessage,
      NextStepSelectValue.NextPrompt,
      NextStepSelectValue.EndAutomation,
      NextStepSelectValue.NewAutomation,
      NextStepSelectValue.OpenLink,
    ]
      .map((value) => getStepValueDisplayProps(value))
      .concat(childAutomationOptions);
  }, [childAutomationOptions, promptType]);

  return { options, isLoading };
};
